import type { SupabaseClient, User } from '@supabase/supabase-js';
import type { AuthUserProfile } from '@/stores/auth-store';
import { lookupUserRecordByIdentityDetailed } from '@/lib/supabase/user-identity';
import { isConfirmedAuthSignOut } from '@/lib/supabase/auth-user';
import { normalizeRole } from '@/lib/rbac';
import {
  CANONICAL_AUTH_SELECT,
  canonicalAuthResult,
  type CanonicalAuthResult,
  type CanonicalUserRow,
} from './canonical-auth-profile';

export type AuthProfileResolution =
  | { kind: 'resolved'; authActorId: string; canonicalUserId: string; profile: AuthUserProfile }
  | { kind: 'blocked'; authActorId: string; reason: 'inactive' | 'missing' | 'missing-role' }
  | { kind: 'unavailable' };

/** Bounds hydration latency; late work cannot commit an identity. */
async function bounded<T>(work: Promise<T>, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<T>((resolve) => {
        timer = setTimeout(() => resolve(fallback), 5_000);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/** Only actor-bound ACTIVE canonical rows supply client tenant/role authority. */
export async function resolveAuthProfile(
  supabase: SupabaseClient,
  user: Pick<User, 'id' | 'email' | 'user_metadata'>,
  syncMetadata: () => Promise<CanonicalAuthResult | null>,
): Promise<AuthProfileResolution> {
  const usable = (result: CanonicalAuthResult | null): AuthProfileResolution => {
    if (!result || result.kind === 'unavailable' || result.authActorId !== user.id) {
      throw new Error('auth_profile_unavailable');
    }
    if (result.kind === 'blocked') return result;
    const role = normalizeRole(result.role);
    if (
      !result.canonicalUserId ||
      !result.tenantId ||
      !role ||
      result.isActive !== true ||
      result.status !== 'ACTIVE'
    ) {
      throw new Error('auth_profile_unavailable');
    }
    return {
      kind: 'resolved',
      authActorId: result.authActorId,
      canonicalUserId: result.canonicalUserId,
      profile: {
        id: result.authActorId,
        email: user.email ?? '',
        firstName: result.firstName,
        lastName: result.lastName,
        role,
        tenantId: result.tenantId,
      },
    };
  };
  return bounded(
    Promise.any([
      (async () => {
        const { record, failed } = await lookupUserRecordByIdentityDetailed<CanonicalUserRow>(
          supabase,
          user,
          CANONICAL_AUTH_SELECT,
        );
        // RLS can hide an inactive row. Only the service-role source can confirm absence.
        return usable(!failed && record ? canonicalAuthResult(record, user.id) : null);
      })(),
      Promise.resolve().then(syncMetadata).then(usable),
    ]).catch((): AuthProfileResolution => ({ kind: 'unavailable' })),
    { kind: 'unavailable' },
  );
}

/** Re-verify the principal immediately before the guard's synchronous commit. */
export async function verifyAuthProfileCommit(
  supabase: SupabaseClient,
  actorId: string,
  result: AuthProfileResolution,
  isCurrent: () => boolean,
): Promise<AuthProfileResolution | { kind: 'signed-out' | 'actor-changed' } | null> {
  if (!isCurrent()) return null;
  if (
    result.kind !== 'unavailable' &&
    (result.authActorId !== actorId ||
      (result.kind === 'resolved' && result.profile.id !== actorId))
  ) {
    return { kind: 'unavailable' };
  }
  const principal = await bounded(
    supabase.auth
      .getUser()
      .then(({ data, error }) => ({
        user: error ? null : data.user,
        signedOut: isConfirmedAuthSignOut(error),
      }))
      .catch(() => null),
    null,
  );
  if (!isCurrent()) return null;
  if (principal?.signedOut) return { kind: 'signed-out' };
  if (!principal?.user) return { kind: 'unavailable' };
  if (principal.user.id !== actorId) return { kind: 'actor-changed' };
  return result;
}
